import React, { useContext, useMemo } from "react";
import { useSelector } from "react-redux";
import { useIntl } from "react-intl";
import { Box, Skeleton, Typography } from "@mui/material";

import AppState from "../../store/AppState";
import { ViewContext } from "../../context/view-context";
import {
  selectAllProductsFetched,
  selectProductAnalyticsDataById,
} from "../../store/product/ProductSelectors";
import {
  selectHeatMapMetric,
  selectHeatMapGradient,
  selectHeatMapBatchPages,
  selectHeatMapIsFetching,
  selectHeatMapFreshProductCodes,
} from "../../store/heatmap/HeatMapSelectors";
import {
  computeColorScales,
  formatHeatMapMetricValue,
} from "../../utils/HeatMapUtils";
import { isHeatMapMetricDateReliant } from "./HeatMapMetricSelect";
import {
  StyledPlanogramPane,
  StyledPlanogramBody,
  StyledPlanogramGridWrapper,
  StyledPlanogramCell,
  StyledColorScale,
  StyledColorScaleBand,
  StyledColorScaleValue,
  StyledPageColumns,
  StyledPageColumn,
  StyledPageColumnLabel,
  StyledPageGrid,
  heatMapModalStyles,
} from "./HeatMapModalStyles";

/* Cell-size constants. */
const CELL_MARGIN = 2;
const SIDE_LENGTH_LG = 40;
const SIDE_LENGTH_MED = 20;
const SIDE_LENGTH_SML = 10;
const BORDER_RADIUS_LG = 5;
const BORDER_RADIUS_MED = 3;
const BORDER_RADIUS_SML = 0;

/**
 * Right pane — renders products from the active heat map batch (≤240
 * across up to 10 catalog pages) as a wrapping row of `P#N` page-columns.
 *
 * Each page-column has a header (`P#1`, `P#2`, …) and a sub-grid of
 * cells laid out using the main grid's column count (ViewContext).
 *
 * Layout wraps to a second row when 10 page-columns don't fit in the
 * modal width`.heat-map-page-container { float: left }`.
 *
 * Cell size is adaptive (40 / 20 / 10 px) based on total page count.
 */
const HeatMapPlanogram: React.FC = () => {
  const intl = useIntl();
  const styles = heatMapModalStyles();
  const view = useContext(ViewContext);
  const columns = view.styles.column;

  const metric = useSelector(selectHeatMapMetric);
  const gradient = useSelector(selectHeatMapGradient);
  const batchPages = useSelector(selectHeatMapBatchPages);
  const isFetching = useSelector(selectHeatMapIsFetching);
  const freshProductCodes = useSelector(selectHeatMapFreshProductCodes);
  const productsById = useSelector(selectAllProductsFetched);

  const isDateReliant = isHeatMapMetricDateReliant(metric);

  // Pull the metric value for every product across all pages of the batch.
  // Per-product freshness: for date-reliant metrics, a value is only
  // considered "fresh" if the product's analyticsId is in
  // `freshProductCodes` (i.e. its chunk has settled for the current range).
  // Non-date-reliant metrics (Inventory, Price, Stock Value, SKU Coverage)
  // come from product catalog data and are always considered fresh.
  const { valuesByProductId, freshByProductId } = useSelector(
    (state: AppState) => {
      const valueMap = new Map<string, number | undefined>();
      const freshMap = new Map<string, boolean>();
      batchPages.pages.forEach((page) => {
        page.productIds.forEach((productId) => {
          const analytics = selectProductAnalyticsDataById(state, productId);
          const found = analytics?.find((a) => a.name === metric);
          const raw = found?.value;
          const numeric =
            typeof raw === "number"
              ? raw
              : typeof raw === "string"
                ? parseFloat(raw)
                : undefined;
          valueMap.set(
            productId,
            Number.isFinite(numeric) ? (numeric as number) : undefined,
          );
          if (!isDateReliant) {
            freshMap.set(productId, true);
          } else {
            const analyticsId = productsById[productId]?.analyticsId;
            freshMap.set(
              productId,
              Boolean(analyticsId && freshProductCodes[analyticsId]),
            );
          }
        });
      });
      return { valuesByProductId: valueMap, freshByProductId: freshMap };
    },
  );

  // Adaptive cell sizing: > 10 → SML, > 3 → MED, else LG`
  // based on number of pages. With ≤10 pages we'll usually land on MED.
  const { sideLength, borderRadius } = useMemo(() => {
    const numOfPages = batchPages.pages.length;
    if (numOfPages > 10) {
      return { sideLength: SIDE_LENGTH_SML, borderRadius: BORDER_RADIUS_SML };
    } else if (numOfPages > 3) {
      return { sideLength: SIDE_LENGTH_MED, borderRadius: BORDER_RADIUS_MED };
    }
    return { sideLength: SIDE_LENGTH_LG, borderRadius: BORDER_RADIUS_LG };
  }, [batchPages.pages.length]);

  // Compute color scales using the metric values across the entire batch,
  // then assign each product its color. One scale spans all pages so the
  // P# columns share a single legend.
  const { scales, productColors } = useMemo(() => {
    // Only fresh values feed the scale so the legend reflects the current
    // range, not stale leftovers from the previous range.
    const values: number[] = [];
    valuesByProductId.forEach((v, productId) => {
      if (v !== undefined && freshByProductId.get(productId)) values.push(v);
    });
    const computedScales = computeColorScales(values, gradient);

    const colorMap = new Map<string, string | undefined>();
    valuesByProductId.forEach((value, productId) => {
      if (value === undefined || !freshByProductId.get(productId)) {
        colorMap.set(productId, undefined);
        return;
      }
      const band = computedScales.find(
        (s) => value <= s.upperBound && value >= s.lowerBound,
      );
      colorMap.set(
        productId,
        band?.color ?? computedScales[computedScales.length - 1]?.color,
      );
    });

    return { scales: computedScales, productColors: colorMap };
  }, [valuesByProductId, freshByProductId, gradient]);

  return (
    <StyledPlanogramPane>
      <StyledPlanogramBody>
        <StyledPlanogramGridWrapper>
          <Typography variant="modalSubtitle">
            {intl.formatMessage({
              id: "heatMap.label",
              defaultMessage: "Heat Map",
            })}
          </Typography>

          <StyledPageColumns>
            {batchPages.pages.map((page, idx) => (
              <StyledPageColumn key={page.pageNumber}>
                <StyledPageColumnLabel>
                  {`P#${idx + 1}`}
                </StyledPageColumnLabel>
                <StyledPageGrid
                  columns={columns}
                  sideLength={sideLength}
                  cellMargin={CELL_MARGIN}
                >
                  {page.productIds.map((productId) => {
                    const isFresh = freshByProductId.get(productId);
                    // Date-reliant cells skeleton when their chunk hasn't
                    // landed for the current range yet (and we're still
                    // fetching). Once not fetching, non-fresh cells render
                    // uncolored instead of staying as skeletons.
                    const showSkeleton =
                      isDateReliant && !isFresh && isFetching;
                    return showSkeleton ? (
                      <Skeleton
                        key={productId}
                        variant="rectangular"
                        animation="wave"
                        width={sideLength}
                        height={sideLength}
                        sx={{ borderRadius: `${borderRadius}px` }}
                        data-testid={`heatmap-skeleton-${productId}`}
                      />
                    ) : (
                      <StyledPlanogramCell
                        key={productId}
                        cellColor={productColors.get(productId)}
                        sideLength={sideLength}
                        borderRadius={borderRadius}
                        data-product-id={productId}
                        data-testid={`heatmap-cell-${productId}`}
                      />
                    );
                  })}
                </StyledPageGrid>
              </StyledPageColumn>
            ))}
          </StyledPageColumns>
        </StyledPlanogramGridWrapper>

        {scales.length > 0 && (
          <Box sx={styles.planogramBox}>
            <Typography variant="heading">{metric}</Typography>
            <StyledColorScale>
              {scales.map((s, idx) => (
                <StyledColorScaleBand key={idx} bandColor={s.color}>
                  <StyledColorScaleValue variant="scaleNumbers">
                    {formatHeatMapMetricValue(s.upperBound)}
                  </StyledColorScaleValue>
                </StyledColorScaleBand>
              ))}
              <Box sx={styles.colorScaleBox}>
                <StyledColorScaleValue variant="scaleNumbers">
                  {formatHeatMapMetricValue(
                    scales[scales.length - 1].lowerBound,
                  )}
                </StyledColorScaleValue>
              </Box>
            </StyledColorScale>
          </Box>
        )}
      </StyledPlanogramBody>

      {batchPages.totalProductCount === 0 && (
        <Box sx={styles.noProductsBox}>
          {intl.formatMessage({
            id: "heatMap.noProducts",
            defaultMessage: "No products to display.",
          })}
        </Box>
      )}
    </StyledPlanogramPane>
  );
};

export default HeatMapPlanogram;
